import { useTranslation } from "react-i18next";
import type { FieldErrors, Noop } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Typography, Alert, Stack } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FieldPaper from "src/components/organisms/FieldPaper/FieldPaper";
import { useEffect, useState } from "react";
import { Loader } from "./Loader.component";

export interface VersionPropTypes {
    step: number;
    onChange: Noop;
    onBlur: Noop;
    errors: FieldErrors;
    exchangeProfile: string;
}

export const Version = ({
    step,
    onChange,
    onBlur,
    errors,
    exchangeProfile,
}: VersionPropTypes) => {
    const { t } = useTranslation();

    const [versionsLoading, setVersionsLoading] = useState(false);
    const [versions, setVersions] = useState<{ versionNumber: string }[]>([]);

    const controller = new AbortController();
    useEffect(() => {
        if (!exchangeProfile) {
            return;
        }
        const fetchVersions = async () => {
            setVersionsLoading(true);

            const apiUrl = encodeURI(
                `${import.meta.env.VITE_PUBLICATIE_API_URL}/v1.1/exchange-profiles/${exchangeProfile}`
            );

            try {
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                });
                const data = await response.json();

                setVersions(data.versions);
            } catch (error) {
                console.error("Error fetching exchange profiles:", error);
            } finally {
                setVersionsLoading(false);
            }
        };

        void fetchVersions();

        return () => {
            controller.abort();
        };
    }, [exchangeProfile]);

    return (
        <FieldPaper
            title={
                <Typography variant="h6">
                    {step}. {t("validated-question-create.version.title")}
                </Typography>
            }
        >
            {versionsLoading ? (
                <Stack alignItems="center">
                    <Loader />
                </Stack>
            ) : versions?.length > 0 ? (
                <FormControl fullWidth>
                    <Select
                        data-testid="version"
                        defaultValue={""}
                        size="small"
                        onChange={onChange}
                        onBlur={onBlur}
                    >
                        {versions.map((item: { versionNumber: string }) => (
                            <MenuItem
                                value={item.versionNumber}
                                key={item.versionNumber}
                            >
                                {item.versionNumber}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        <ErrorMessage errors={errors} name="version" />
                    </FormHelperText>
                </FormControl>
            ) : (
                <Alert severity="info">
                    {t("validated-question-create.version.empty-state")}
                </Alert>
            )}
        </FieldPaper>
    );
};

export default Version;
